import React, { useEffect, useState } from "react";
import { vendorAnalytics } from "@/lib/vendorApi";
import { Eye, Heart, Package, Wrench, Store, Clock, TrendingUp, BarChart3, MessageCircle } from "lucide-react";

export default function VendorAnalytics({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("7d");

  useEffect(() => {
    setLoading(true);
    setError(null);
    vendorAnalytics()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Could not load analytics"))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) {
    return <div className="bg-white border border-slate-100 rounded-2xl p-10 text-center text-slate-500 text-sm" data-testid="analytics-loading">Loading analytics...</div>;
  }
  if (error) {
    return <div className="bg-white border border-red-100 rounded-2xl p-6 text-red-700 text-sm" data-testid="analytics-error">{error}</div>;
  }
  if (!data) return null;

  const { summary, views_7d, views_30d, top_products, top_services, busy_hours, peak_hour, has_data, whatsapp_clicks } = data;
  const trend = range === "7d" ? views_7d : views_30d;

  return (
    <div className="space-y-6" data-testid="vendor-analytics">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold">Analytics</h2>
          <p className="text-sm text-slate-500">Real-time insight into your shop's activity.</p>
        </div>
      </div>

      {!has_data && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5" data-testid="analytics-empty-banner">
          <div className="flex items-start gap-3">
            <BarChart3 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">Not enough data yet</div>
              <p className="text-sm text-amber-800 mt-1">Once customers start viewing your products, services or shop page, insights will show up here automatically.</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="analytics-summary">
        <SummaryCard testid="metric-total-views" icon={Eye} label="Total Views" value={summary.total_views} color="emerald" />
        <SummaryCard testid="metric-product-views" icon={Package} label="Product Views" value={summary.product_views} color="emerald" />
        <SummaryCard testid="metric-service-views" icon={Wrench} label="Service Views" value={summary.service_views} color="amber" />
        <SummaryCard testid="metric-shop-views" icon={Store} label="Shop Views" value={summary.shop_views} color="sky" />
        <SummaryCard testid="metric-likes" icon={Heart} label="Likes" value={summary.total_likes} color="rose" />
      </div>

      {/* Views trend */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-1 inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Views trend</div>
            <h3 className="font-display text-lg font-bold">{range === "7d" ? "Last 7 days" : "Last 30 days"}</h3>
          </div>
          <div className="inline-flex bg-slate-100 rounded-full p-1 text-xs font-semibold">
            <button onClick={() => setRange("7d")} className={`px-3 py-1 rounded-full ${range === "7d" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="range-7d">7 days</button>
            <button onClick={() => setRange("30d")} className={`px-3 py-1 rounded-full ${range === "30d" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="range-30d">30 days</button>
          </div>
        </div>
        <TrendBars data={trend} testid={`trend-${range}`} />
      </div>

      {/* Top products / services */}
      <div className="grid lg:grid-cols-2 gap-4">
        <TopList title="Top Products" icon={Package} items={top_products} emptyLabel="No product views yet." testid="top-products" />
        <TopList title="Top Services" icon={Wrench} items={top_services} emptyLabel="No service views yet." testid="top-services" />
      </div>

      {/* WhatsApp clicks per product */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5" data-testid="whatsapp-clicks">
        <h3 className="font-display text-lg font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-700" /> WhatsApp Orders Initiated</h3>
        <p className="text-xs text-slate-500 mb-3">Kitni baar customers ne "WhatsApp pe Order Karo" tap kiya (message bheja ya nahi, ye count nahi hota).</p>
        {whatsapp_clicks && whatsapp_clicks.length > 0 ? (
          <div className="space-y-2">
            {whatsapp_clicks.map((it) => (
              <div key={it.id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-2" data-testid={`wa-clicks-row-${it.id}`}>
                <img src={it.image || "https://placehold.co/48x48/f1f5f9/94a3b8?text=?"} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0 text-sm font-semibold truncate">{it.name}</div>
                <div className="text-sm font-bold text-emerald-800 whitespace-nowrap" data-testid={`wa-clicks-count-${it.id}`}>{it.clicks} clicks</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-4 text-center">No WhatsApp clicks yet.</div>
        )}
      </div>

      {/* Busy hours */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-amber-700 mb-1 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Busy hours</div>
            <h3 className="font-display text-lg font-bold">When customers browse</h3>
          </div>
          {peak_hour !== null && peak_hour !== undefined && (
            <div className="text-xs text-slate-600 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-full font-semibold" data-testid="peak-hour">
              Peak: {String(peak_hour).padStart(2, "0")}:00 - {String((peak_hour + 1) % 24).padStart(2, "0")}:00
            </div>
          )}
        </div>
        <BusyHoursChart data={busy_hours} />
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color, testid }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-800",
    amber: "bg-amber-50 text-amber-800",
    sky: "bg-sky-50 text-sky-800",
    rose: "bg-rose-50 text-rose-800",
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4" data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${colors[color] || colors.emerald}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase text-slate-400 tracking-wider">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function TrendBars({ data, testid }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div data-testid={testid}>
      <div className="flex items-end gap-1 h-32" role="img" aria-label="Views trend">
        {data.map((d) => {
          const pct = (d.count / max) * 100;
          const day = d.date.slice(-2);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0" title={`${d.date}: ${d.count} views`}>
              <div className="w-full rounded-t bg-emerald-500 hover:bg-emerald-700 transition-colors" style={{ height: `${Math.max(pct, 2)}%` }} />
              <div className="text-[9px] text-slate-400 truncate">{day}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-xs text-slate-500 flex justify-between">
        <span>Total: <span className="font-semibold text-slate-700">{total}</span></span>
        <span>Days: {data.length}</span>
      </div>
    </div>
  );
}

function TopList({ title, icon: Icon, items, emptyLabel, testid }) {
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-emerald-700" /> {title}</h3>
      {items && items.length > 0 ? (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-2">
              <div className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
              <img src={it.image || "https://placehold.co/48x48/f1f5f9/94a3b8?text=?"} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{it.name}</div>
                <div className="text-xs text-slate-500">{it.views} views{it.likes ? ` · ${it.likes} likes` : ""}</div>
              </div>
              {it.price !== null && it.price !== undefined && <div className="text-sm font-bold text-emerald-800">₹{it.price}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-500 py-4 text-center">{emptyLabel}</div>
      )}
    </div>
  );
}

function BusyHoursChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) {
    return <div className="text-sm text-slate-500 py-4 text-center" data-testid="busy-hours-empty">Not enough activity yet to detect busy hours.</div>;
  }
  return (
    <div data-testid="busy-hours-chart">
      <div className="flex items-end gap-[2px] h-24">
        {data.map((d) => {
          const pct = (d.count / max) * 100;
          return (
            <div key={d.hour} className="flex-1 flex flex-col items-center justify-end min-w-0" title={`${String(d.hour).padStart(2, "0")}:00 – ${d.count} views`}>
              <div className="w-full rounded-t bg-amber-400" style={{ height: `${Math.max(pct, d.count > 0 ? 5 : 0)}%` }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
      </div>
    </div>
  );
}
